import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Avatar, Button, Skeleton, TintableIcon } from '@/components/ui';
import { AppLogo } from '@/components/icons/AppLogo';
import { useAppAuth } from '@/lib/useAppAuth';
import { useAppUser } from '@/lib/useAppUser';
import { useKycStatus } from '@/lib/kyc/useKyc';
import { useKycIdentityPending } from '@/lib/kyc/useKycIdentityPending';
import { useTradingPositionGetPortfolio, useWithdrawalGetBalance } from '@/lib/endpoints';
import { dollarsToCents, formatCentsGrouped } from '@/lib/format-currency';
import { resolveActiveItem, sideNavItems } from '../BottomNav/navItems';
import { SideNavNotifications } from './SideNavNotifications';

import verifyBadge from '@/assets/settings/verify-badge.svg';
import dollarSign from '@/assets/dollar-sign.svg';
import plusIcon from '@/assets/plus.svg';

const TEST_ID_PREFIX = 'side-nav';

/** Thin horizontal rule between sidebar sections (Figma divider). */
function Divider() {
  return <div aria-hidden="true" className="h-px w-full shrink-0 bg-input-soft" />;
}

/**
 * User avatar with the KYC verify badge, linking to settings. Mirrors the
 * settings {@link AccountHeader} avatar (same badge gating: identity KYC
 * satisfied once /kyc/status has loaded).
 */
function ProfileAvatar() {
  const { isAuthenticated } = useAppAuth();
  const user = useAppUser();
  const { data: kyc } = useKycStatus(isAuthenticated);
  const pendingIdentity = useKycIdentityPending();
  const isVerified = Boolean(kyc) && pendingIdentity === null;

  return (
    <Link
      to="/settings"
      aria-label="Open settings"
      data-testid={`${TEST_ID_PREFIX}-profile`}
      className="relative rounded-full"
    >
      <Avatar src={user?.profileImage} alt="" className="size-20 border-[3.5px] border-input-soft bg-surface" />
      {isVerified && <img src={verifyBadge} alt="Verified account" className="absolute -bottom-0.5 -right-1 size-6" />}
    </Link>
  );
}

/** Wallet cash balance pill with the "Add coins" action (Figma "Balance"). */
function BalancePill() {
  const user = useAppUser();
  const navigate = useNavigate();
  // Same live-updating query the top bar uses (patched by useWalletBalanceRealtime).
  const { data, isLoading } = useWithdrawalGetBalance({ query: { enabled: !!user } });

  if (isLoading) return <Skeleton className="h-[50px] w-full rounded-pill" />;

  return (
    <div
      data-testid={`${TEST_ID_PREFIX}-balance`}
      className="flex h-[50px] w-full items-center gap-2.5 rounded-pill border-2 border-input-soft bg-surface px-5"
    >
      {/* Masked over currentColor so the glyph follows the theme (the raw
          svg has a near-black fill baked in, which vanishes in dark mode). */}
      <TintableIcon src={dollarSign} className="size-5 text-ink" />
      <span data-testid={`${TEST_ID_PREFIX}-balance-value`} className="flex-1 truncate text-xl tracking-tight text-ink">
        {/* Two decimals always ("0.00", "1,234.50") — same formatting as the mobile TopBar. */}
        {data?.balance != null ? formatCentsGrouped(dollarsToCents(data.balance)) : ''}
      </span>
      <Button
        data-testid={`${TEST_ID_PREFIX}-add-coins-button`}
        variant="ghost"
        size="icon"
        className="size-[37px]"
        aria-label="Add coins"
        onClick={() => navigate('/wallet')}
      >
        <img src={plusIcon} alt="" className="size-5 shrink-0" />
      </Button>
    </div>
  );
}

/**
 * Unrealized-PnL chip shown next to the Portfolio item (Figma "▲ $82.15").
 * Hidden while flat (no open exposure) so the row stays quiet by default.
 */
function PortfolioPnlChip() {
  const { data: portfolio } = useTradingPositionGetPortfolio();
  const pnl = portfolio?.totalUnrealizedPnl ?? 0;
  if (pnl === 0) return null;
  const up = pnl > 0;

  return (
    <span
      data-testid={`${TEST_ID_PREFIX}-portfolio-pnl`}
      className={cn('flex items-center gap-0.5 text-xs font-semibold', up ? 'text-up' : 'text-down')}
    >
      <svg viewBox="0 0 10 9" aria-hidden="true" className={cn('h-[9px] w-2.5 fill-current', !up && 'rotate-180')}>
        <path d="M5 0 L10 9 L0 9 Z" />
      </svg>
      ${Math.abs(pnl).toFixed(2)}
    </span>
  );
}

/**
 * Desktop navigation sidebar (Figma "Side menu", node 6606:189120).
 *
 * Fixed card floating 24px off the viewport edges at md+ (hidden on mobile,
 * where {@link BottomNav} takes over). Owns the brand logo, profile avatar,
 * balance pill, primary navigation, and the notifications entry point — the
 * top bar hides its duplicates of these at the same breakpoint.
 *
 * The content column clears it via `#root { margin-left: var(--sidebar-width) }`
 * (see index.css), so the card width and `--sidebar-width` move together.
 * Mounted by the app shell only when authenticated.
 */
export function SideNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = resolveActiveItem(pathname);

  return (
    <nav
      data-testid={TEST_ID_PREFIX}
      aria-label="Primary"
      className={cn(
        'fixed bottom-6 left-6 top-6 z-40 w-[286px] hidden md:flex flex-col gap-6 p-6',
        'rounded-xl border-[1.5px] border-input-soft bg-surface/70 backdrop-blur-md',
      )}
    >
      <Link to="/" aria-label="Go to home" className="inline-block w-fit shrink-0">
        <AppLogo width={200} disableAnimation />
      </Link>

      <div className="flex w-full shrink-0 flex-col items-center gap-4 pt-4">
        <ProfileAvatar />
        <BalancePill />
      </div>

      <Divider />

      {/* Middle section scrolls on short viewports so the header and the notifications cell stay put. */}
      <div className="flex min-h-0 w-full flex-1 flex-col gap-4 overflow-y-auto">
        {sideNavItems.map((item) => {
          const isActive = item.id === active;
          const { Icon } = item;
          return (
            <button
              key={item.id}
              data-testid={`${TEST_ID_PREFIX}-item-${item.id}`}
              onClick={() => navigate(item.path)}
              className={cn(
                'flex h-11 w-full shrink-0 items-center gap-2.5 rounded-lg px-4 transition-colors',
                isActive ? (item.activeClassName ?? 'text-purple') : 'text-ink-muted hover:bg-primary/[0.03]',
              )}
            >
              <span className="flex size-5 shrink-0 items-center justify-center">
                <Icon className="size-full" active={isActive} />
              </span>
              <span className={cn('text-base tracking-[0.32px]', isActive ? 'font-medium' : 'font-normal')}>
                {item.label}
              </span>
              {item.id === 'portfolio' && <PortfolioPnlChip />}
            </button>
          );
        })}
      </div>

      <div className="flex w-full shrink-0 flex-col gap-4">
        <Divider />
        <SideNavNotifications />
      </div>
    </nav>
  );
}
